<template>
  <main class="faq-page">
    <section class="faq-hero">
      <span class="eyebrow">HELP CENTER</span>
      <h1>常见问题</h1>
      <p>关于虚拟机、积分计费、场景部署和账户安全的常见答案。</p>
      <div class="search-box">
        <span>⌕</span>
        <input v-model.trim="keyword" type="search" placeholder="搜索问题，例如：积分、延期、Docker" />
        <button v-if="keyword" type="button" @click="keyword = ''">清除</button>
      </div>
      <div class="quick-links">
        <router-link to="/multi-instances">创建虚拟机</router-link>
        <router-link to="/scenario-lab">部署场景</router-link>
        <router-link to="/recharge-points">充值积分</router-link>
        <router-link to="/support-ticket">提交工单</router-link>
      </div>
    </section>

    <section class="faq-shell">
      <nav class="category-nav" aria-label="问题分类">
        <button
          v-for="category in categories"
          :key="category.id"
          type="button"
          :class="{ active: activeCategory === category.id }"
          @click="activeCategory = category.id"
        >
          <span>{{ category.icon }}</span>{{ category.label }}
          <small>{{ countByCategory(category.id) }}</small>
        </button>
      </nav>

      <div class="faq-content">
        <div class="result-head">
          <div>
            <h2>{{ currentCategoryLabel }}</h2>
            <p v-if="keyword">找到 {{ filteredQuestions.length }} 条与“{{ keyword }}”相关的结果</p>
            <p v-else>点击问题查看详细说明</p>
          </div>
          <span>{{ filteredQuestions.length }} 个问题</span>
        </div>

        <el-collapse v-if="filteredQuestions.length" v-model="openItems" accordion class="faq-list">
          <el-collapse-item v-for="item in filteredQuestions" :key="item.id" :name="item.id">
            <template #title><span class="question-title">{{ item.question }}</span></template>
            <div class="answer" v-html="item.answer"></div>
          </el-collapse-item>
        </el-collapse>

        <div v-else class="empty-state">
          <strong>没有找到相关问题</strong>
          <p>换一个关键词试试，或者直接提交工单告诉我们你的问题。</p>
          <router-link to="/support-ticket">提交工单</router-link>
        </div>

        <div class="support-card">
          <div><strong>仍然没有解决？</strong><p>环境故障请附上任务 ID、实例 ID、发生时间和错误信息，以便更快定位。</p></div>
          <div class="support-actions"><router-link to="/support-ticket">提交工单</router-link><a href="mailto:admin@azlinux.com">发送邮件</a></div>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
export default {
  name: "FAQ",
  data() {
    return {
      keyword: "",
      activeCategory: "all",
      openItems: "",
      categories: [
        { id: "all", label: "全部问题", icon: "▦" },
        { id: "environment", label: "虚拟机环境", icon: "⌘" },
        { id: "billing", label: "积分与计费", icon: "¥" },
        { id: "scenario", label: "场景部署", icon: "◇" },
        { id: "account", label: "账户与安全", icon: "○" },
      ],
      questions: [
        { id: "position", category: "account", question: "运维练兵场适合哪些用户？", answer: "面向 SRE、运维工程师、系统管理员、DevOps 工程师及希望进行 Linux 实战练习的学习者。平台提供临时云实例、场景自动部署和运维知识内容，适合按需练习，不建议把实验实例用于生产业务。" },
        { id: "create-time", category: "environment", question: "创建虚拟机通常需要多久？", answer: "多数环境可在约 1 分钟内完成，但实际时间会受到云厂商库存、Spot 实例价格、镜像和网络状态影响。页面计时器会展示本次创建耗时。" },
        { id: "create-failed", category: "environment", question: "为什么创建虚拟机有时会失败？", answer: "常见原因包括所选可用区库存不足、Spot 价格或云 API 临时限流。系统会尽可能进行重试；如果仍失败，可以稍后重试、减少实例数量或更换规格。预冻结但未实际消费的积分应按任务结果解冻。" },
        { id: "spot", category: "environment", question: "Spot 实例会不会被云厂商回收？", answer: "存在被回收的可能。Spot 实例价格较低，适合短期练习，但不适合保存唯一数据或承载生产业务。重要练习文件请及时下载到本地。" },
        { id: "release", category: "environment", question: "环境到期后会发生什么？", answer: "实例到达释放时间后会被系统释放，实例内数据可能无法恢复。需要继续使用时，请在到期前使用“延期释放”，并确保账户可用积分大于 0 且足以承担后续资源费用。" },
        { id: "files", category: "environment", question: "如何上传或下载练习文件？", answer: "在多实例页面使用“上传文件”或“下载文件”。上传支持选择多个本地文件，但一次选择一台目标实例；文件默认传输到 sre 用户的 HOME 目录。请勿上传生产密钥、个人敏感信息或违法内容。" },
        { id: "os", category: "environment", question: "目前支持哪些操作系统？", answer: "多实例环境仅提供 Linux 发行版，不提供 Windows。实际可选发行版和版本以创建页面显示为准；不同场景模板可能只支持其中一部分系统。" },
        { id: "points-rate", category: "billing", question: "人民币与积分如何换算？", answer: "当前标准换算为 <strong>1 元 = 100 积分</strong>，即 1 积分 = 0.01 元。充值页展示的到账积分和最终确认信息为准。" },
        { id: "billing-items", category: "billing", question: "哪些资源会消耗积分？", answer: "云计算实例、系统盘或新增磁盘等资源会按照平台计费规则消耗积分。所有用户使用云资源都会正常计费；VIP 权益不代表云资源免费。具体费率和流水可在充值页与消费明细中查看。" },
        { id: "vip", category: "billing", question: "网站如何收取积分？", answer: "收费项目尽量保持简单：虚拟机收取资源使用费，自动化场景在部署成功后收取一次场景部署费。创建虚拟机本身不收启动费。" },
        { id: "freeze", category: "billing", question: "为什么创建环境前要预冻结积分？", answer: "预冻结用于确认余额能够覆盖一段基础使用时间，避免资源已创建但余额不足。冻结积分不是重复扣费，任务失败或资源结算后会根据实际结果处理。" },
        { id: "insufficient", category: "billing", question: "积分不足时哪些操作会受限？", answer: "积分不足时不能创建新环境、扩容磁盘或延期释放。正在运行的资源仍可能继续产生费用，请及时充值或主动销毁不再使用的环境。" },
        { id: "refund", category: "billing", question: "余额或充值可以申请退款吗？", answer: "如需退款，请提交“账号/计费”工单。我们会核对充值订单、已消费资源和剩余积分后处理；具体结果以核验情况和适用规则为准。" },
        { id: "scenario-what", category: "scenario", question: "什么是场景部署？", answer: "场景部署可以把你当前拥有的虚拟机自动配置成 Docker、Nginx 等可练习环境，并进行基础健康检查。请先在多实例页面创建符合节点数和操作系统要求的实例。" },
        { id: "scenario-failed", category: "scenario", question: "场景部署失败会扣费吗？", answer: "不会。场景部署费只在部署成功后扣除，失败时自动解除预冻结；虚拟机运行期间产生的资源使用费仍会正常计算。" },
        { id: "template-request", category: "scenario", question: "可以申请新的软件或集群模板吗？", answer: "可以。请通过工单选择“软件模板需求”，写明软件名称、版本、节点数量、目标操作系统、是否需要预置数据以及希望练习的内容。" },
        { id: "share", category: "account", question: "账号可以共享给其他人使用吗？", answer: "不建议也不允许多人共享账户。共享会带来资源误删、积分消费争议和凭据泄露风险。每位使用者应注册独立账户。" },
        { id: "password", category: "account", question: "可以把生产密码或 SSH 私钥放到实验环境吗？", answer: "不可以。实验环境是临时资源，请使用专门的练习凭据，不要上传生产密码、SSH 私钥、商业秘密或其他敏感数据。" },
        { id: "delete-account", category: "account", question: "如何申请注销账户或删除个人信息？", answer: "请使用注册邮箱发送邮件至 <a href='mailto:admin@azlinux.com'>admin@azlinux.com</a>，或提交账号类工单。核验身份后我们会按照隐私政策处理。" },
      ],
    };
  },
  computed: {
    filteredQuestions() {
      const key = this.keyword.toLowerCase();
      return this.questions.filter((item) => {
        const categoryMatch = this.activeCategory === "all" || item.category === this.activeCategory;
        const keywordMatch = !key || `${item.question} ${item.answer}`.toLowerCase().includes(key);
        return categoryMatch && keywordMatch;
      });
    },
    currentCategoryLabel() {
      return this.categories.find((item) => item.id === this.activeCategory)?.label || "全部问题";
    },
  },
  methods: {
    countByCategory(category) {
      return category === "all" ? this.questions.length : this.questions.filter((item) => item.category === category).length;
    },
  },
};
</script>

<style scoped>
.faq-page { min-height: calc(100vh - 70px); padding: 44px 24px 76px; background: #f4f7fb; color: #203348; text-align: left; }.faq-hero, .faq-shell { width: min(1100px, 100%); margin-inline: auto; }.faq-hero { text-align: center; }.eyebrow { color: #2878d0; font-size: 12px; font-weight: 800; letter-spacing: .12em; }.faq-hero h1 { margin: 8px 0 10px; font-size: clamp(34px, 5vw, 48px); letter-spacing: -.04em; }.faq-hero > p { margin: 0; color: #68788a; font-size: 16px; }.search-box { display: flex; align-items: center; width: min(660px, 100%); height: 52px; margin: 26px auto 15px; padding: 0 17px; border: 1px solid #d7e2ed; border-radius: 12px; background: #fff; box-shadow: 0 8px 25px rgba(42,70,100,.06); }.search-box span { color: #6f8091; font-size: 25px; }.search-box input { flex: 1; min-width: 0; padding: 0 13px; border: 0; outline: 0; color: #25394e; font: inherit; }.search-box button { border: 0; background: none; color: #2878d0; cursor: pointer; }.quick-links { display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; }.quick-links a { padding: 6px 11px; border-radius: 7px; background: #eaf3fc; color: #3972aa; font-size: 13px; text-decoration: none; }
.faq-shell { display: grid; grid-template-columns: 205px minmax(0,1fr); gap: 24px; margin-top: 38px; }.category-nav { align-self: start; display: grid; gap: 5px; padding: 10px; border: 1px solid #e0e8f0; border-radius: 13px; background: #fff; box-shadow: 0 7px 24px rgba(34,64,98,.04); }.category-nav button { display: grid; grid-template-columns: 24px 1fr auto; align-items: center; gap: 8px; padding: 11px 10px; border: 0; border-radius: 8px; background: transparent; color: #617286; text-align: left; cursor: pointer; }.category-nav button:hover, .category-nav button.active { background: #edf5fd; color: #2878d0; }.category-nav small { min-width: 22px; padding: 2px 5px; border-radius: 9px; background: #eef1f5; color: #7c8997; text-align: center; }.faq-content { min-width: 0; }.result-head { display: flex; justify-content: space-between; align-items: flex-end; gap: 20px; margin-bottom: 14px; }.result-head h2 { margin: 0 0 5px; font-size: 23px; }.result-head p, .result-head > span { margin: 0; color: #8290a0; font-size: 13px; }
.faq-list { overflow: hidden; padding: 0 20px; border: 1px solid #e0e8f0; border-radius: 13px; background: #fff; }.question-title { padding: 5px 12px 5px 0; color: #2c4055; font-weight: 700; }.answer { padding: 0 10px 18px 0; color: #607185; font-size: 14px; line-height: 1.85; }.answer :deep(a) { color: #2878d0; }.empty-state { padding: 60px 25px; border: 1px dashed #ccd8e4; border-radius: 13px; background: #fff; text-align: center; }.empty-state strong { font-size: 18px; }.empty-state p { color: #7a8999; }.empty-state a { color: #2878d0; }.support-card { display: flex; justify-content: space-between; align-items: center; gap: 24px; margin-top: 20px; padding: 22px 24px; border: 1px solid #d7e5f3; border-radius: 13px; background: linear-gradient(145deg,#fff,#f2f8ff); }.support-card p { margin: 6px 0 0; color: #6d7d8f; font-size: 13px; }.support-actions { display: flex; gap: 9px; flex: 0 0 auto; }.support-actions a { padding: 9px 13px; border: 1px solid #bcd0e3; border-radius: 8px; color: #326b9f; font-size: 13px; text-decoration: none; }.support-actions a:first-child { border-color: #2878d0; background: #2878d0; color: #fff; }
@media (max-width: 760px) { .faq-page { padding: 28px 14px 50px; }.faq-shell { grid-template-columns: 1fr; margin-top: 28px; }.category-nav { grid-template-columns: repeat(2,1fr); }.result-head > span { display: none; }.faq-list { padding: 0 14px; }.support-card { align-items: flex-start; flex-direction: column; }.support-actions { width: 100%; }.support-actions a { flex: 1; text-align: center; } }
</style>
